import React, { useEffect, useMemo, useState } from "react";
import { ClipboardText, MagnifyingGlass, ArrowsCounterClockwise } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { api } from "@/lib/api";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { toast } from "sonner";

const ACTION_META = {
  "truck.create": { label: "Camion créé", color: "#16A34A" },
  "truck.update": { label: "Camion modifié", color: "#0EA5E9" },
  "truck.delete": { label: "Camion supprimé", color: "#DC2626" },
  "truck.photo.add": { label: "Photo ajoutée", color: "#10B981" },
  "truck.photo.remove": { label: "Photo retirée", color: "#71717A" },
  "impact.create": { label: "Impact ajouté", color: "#EA580C" },
  "impact.update": { label: "Impact modifié", color: "#F59E0B" },
  "impact.delete": { label: "Impact supprimé", color: "#DC2626" },
  "comment.create": { label: "Commentaire", color: "#7C3AED" },
  "comment.delete": { label: "Comm. supprimé", color: "#71717A" },
  "driver.create": { label: "Chauffeur créé", color: "#16A34A" },
  "driver.update": { label: "Chauffeur modifié", color: "#0EA5E9" },
  "driver.delete": { label: "Chauffeur supprimé", color: "#DC2626" },
  "appointment.create": { label: "RDV créé", color: "#0EA5E9" },
  "appointment.update": { label: "RDV modifié", color: "#F59E0B" },
  "appointment.delete": { label: "RDV supprimé", color: "#DC2626" },
  "claim.create": { label: "Sinistre créé", color: "#DC2626" },
  "claim.update": { label: "Sinistre modifié", color: "#F59E0B" },
  "claim.delete": { label: "Sinistre supprimé", color: "#71717A" },
  "user.create": { label: "Utilisateur ajouté", color: "#16A34A" },
  "user.update": { label: "Utilisateur modifié", color: "#0EA5E9" },
  "user.delete": { label: "Utilisateur supprimé", color: "#DC2626" },
  "user.password": { label: "MDP réinitialisé", color: "#7C3AED" },
  "auth.password": { label: "Son MDP changé", color: "#7C3AED" },
  "owner.create": { label: "Propriétaire ajouté", color: "#0EA5E9" },
};

function fmt(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

export default function ActivityPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/activity?limit=500`);
      setEntries(data);
    } catch (e) {
      if (!silent) toast.error("Erreur de chargement (réservé aux admins)");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(() => load({ silent: true }), 20000);

  const users = useMemo(() => {
    const u = new Map();
    for (const e of entries) {
      if (e.user_email) u.set(e.user_email, e.user_name || e.user_email);
    }
    return Array.from(u.entries()).map(([email, name]) => ({ email, name }));
  }, [entries]);

  const actions = useMemo(() => {
    const a = new Set();
    for (const e of entries) a.add(e.action.split(".")[0]);
    return Array.from(a).sort();
  }, [entries]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return entries.filter((e) => {
      if (userFilter !== "all" && e.user_email !== userFilter) return false;
      if (actionFilter !== "all" && !e.action.startsWith(actionFilter)) return false;
      if (!q) return true;
      const text = [e.user_email, e.user_name, e.action, e.summary, e.entity_id, e.entity_label]
        .filter(Boolean).join(" ").toLowerCase();
      return text.includes(q);
    });
  }, [entries, query, userFilter, actionFilter]);

  const countByUser = useMemo(() => {
    const m = {};
    for (const e of entries) {
      const k = e.user_email || "—";
      m[k] = (m[k] || 0) + 1;
    }
    return m;
  }, [entries]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-8 paper-bg min-h-screen" data-testid="activity-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="label-tag">NY Trans · Administration</div>
          <h1 className="serif-display text-4xl md:text-5xl mt-1">
            <ClipboardText size={28} weight="bold" className="inline mr-2 -mt-1 text-[var(--ny-gold)]" />
            Journal d&apos;activité
          </h1>
          <div className="ny-divider w-32 mt-2" />
          <p className="text-zinc-700 mt-3 max-w-xl text-sm">
            Historique de toutes les actions des utilisateurs (création, modification, suppression).
            Réservé aux administrateurs.
          </p>
        </div>
        <button
          onClick={load}
          className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-black bg-white hover:bg-black hover:text-white inline-flex items-center gap-2"
          data-testid="activity-refresh"
        >
          <ArrowsCounterClockwise size={14} weight="bold" /> Rafraîchir
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-300 p-3 mb-4 space-y-3" data-testid="activity-filters">
        <div className="relative">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Recherche email, action, plaque, ID..."
            className="pl-9 rounded-none border-zinc-300"
            data-testid="activity-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <div className="label-tag self-center mr-2">Utilisateur</div>
          <button
            onClick={() => setUserFilter("all")}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border ${userFilter === "all" ? "bg-black text-white border-black" : "bg-white text-black border-zinc-300 hover:border-black"}`}
            data-testid="activity-user-all"
          >
            Tous · {entries.length}
          </button>
          {users.map((u) => (
            <button
              key={u.email}
              onClick={() => setUserFilter(u.email)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border inline-flex items-center gap-1.5 ${userFilter === u.email ? "bg-black text-white border-black" : "bg-white text-black border-zinc-300 hover:border-black"}`}
              data-testid={`activity-user-${u.email}`}
            >
              {u.name}
              <span className={`px-1.5 text-[10px] mono ${userFilter === u.email ? "bg-white text-black" : "bg-zinc-200"}`}>
                {countByUser[u.email] || 0}
              </span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-zinc-200">
          <div className="label-tag self-center mr-2">Action</div>
          <button
            onClick={() => setActionFilter("all")}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border ${actionFilter === "all" ? "bg-black text-white border-black" : "bg-white text-black border-zinc-300 hover:border-black"}`}
          >
            Toutes
          </button>
          {actions.map((a) => (
            <button
              key={a}
              onClick={() => setActionFilter(a)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border ${actionFilter === a ? "bg-black text-white border-black" : "bg-white text-black border-zinc-300 hover:border-black"}`}
              data-testid={`activity-action-${a}`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-zinc-300">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-black text-white">
          <div className="col-span-3 label-tag !text-zinc-400">Date</div>
          <div className="col-span-3 label-tag !text-zinc-400">Utilisateur</div>
          <div className="col-span-3 label-tag !text-zinc-400">Action</div>
          <div className="col-span-3 label-tag !text-zinc-400">Cible</div>
        </div>
        {loading && (
          <div className="px-4 py-10 text-center text-sm text-zinc-500">Chargement…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-zinc-500">Aucune activité enregistrée.</div>
        )}
        {!loading && filtered.map((e) => {
          const meta = ACTION_META[e.action] || { label: e.action, color: "#52525B" };
          return (
            <div
              key={e.id}
              className="grid grid-cols-12 gap-3 px-4 py-3 border-t border-zinc-200 items-center hover:bg-zinc-50"
              data-testid={`activity-row-${e.id}`}
            >
              <div className="col-span-12 md:col-span-3 mono text-xs">{fmt(e.date)}</div>
              <div className="col-span-6 md:col-span-3">
                <div className="text-sm font-bold uppercase truncate">{e.user_name || "—"}</div>
                <div className="text-[10px] text-zinc-500 mono truncate">{e.user_email}</div>
              </div>
              <div className="col-span-6 md:col-span-3">
                <span
                  className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white"
                  style={{ background: meta.color }}
                >
                  {meta.label}
                </span>
                <div className="text-[10px] mono text-zinc-500 mt-1">{e.action}</div>
              </div>
              <div className="col-span-12 md:col-span-3 text-xs text-zinc-700">
                {e.entity_label || e.entity_id || "—"}
                {e.meta?.method && (
                  <div className="text-[10px] mono text-zinc-400 mt-0.5">
                    {e.meta.method} · {e.meta.status}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
